import { Blockquote, H1, H2, Li, P, Strong, Ul } from '../Prose';
export default function SectionDevelopmentWorkflow() {
  return (
    <>
      <P>Development</P>
      <H1>Workflow</H1>

      <P>
        This covers the practices that should be followed while working on
        projects with Git, and submitting changes via GitHub.
      </P>

      <H2>Branches and Commits</H2>

      <P>
        Create a branch for your work (e.g. a new feature or bugfix) off the
        current master branch (or equivalent).
      </P>

      <P>
        There is no specific convention for branch names, just make it something
        descriptive e.g. <Strong>paypal-integration</Strong> or{' '}
        <Strong>fix-logout</Strong>.
      </P>

      <P>
        Even though your branch will eventually be squashed, avoid creating too
        many commits as it will just make things like rebasing harder.
      </P>

      <P>
        If your branch's commit history have got quite large / experienced large
        refactors, you may even want to make use of Git's interactive rebase to
        clean up your branch (e.g. squashing unnecessary history into a single
        commit).
      </P>

      <P>
        Unless there is some useful reason to have multiple commits (e.g.
        breaking down the task into logical chunks), there's nothing wrong with
        squashing the history together to simplify things.
      </P>

      <H2>Creating a Pull Request</H2>

      <P>
        Use the conventional commits standard (following it exactly -- no
        changes to spacing/punctuation etc) to name your PRs, with the following
        types:
      </P>

      <Ul>
        <Li>
          <Strong>build</Strong>: Changes or fixes to the build step, including
          updating external libraries.
        </Li>

        <Li>
          <Strong>ci</Strong>: Changes or fixes to the CI configuration (e.g.
          CircleCI) which shouldn't impact runtime behaviour.
        </Li>

        <Li>
          <Strong>docs</Strong>: Additions or changes to project documentation
          only (e.g. readme, docblock comments).
        </Li>

        <Li>
          <Strong>feat</Strong>: Substantial new functionality, screen, change
          in behaviour, etc.
        </Li>

        <Li>
          <Strong>fix</Strong>: Solving a bug
        </Li>

        <Li>
          <Strong>perf</Strong>: A code change solely for improving performance
        </Li>

        <Li>
          <Strong>refactor</Strong>: A code change which shouldn't impact
          behaviour (i.e. cleaning up duplicate code, clearer naming).
        </Li>

        <Li>
          <Strong>release</Strong>: Bumping the version or build number.
        </Li>

        <Li>
          <Strong>style</Strong>: Formatting changes to code that doesn't affect
          runtime behaviour, e.g. running auto-formatting.
        </Li>

        <Li>
          <Strong>test</Strong>: Additional test coverage or fixes to test,
          without impacting runtime behaviour.
        </Li>

        <Li>
          <Strong>tweak</Strong>: A change so small it shouldn't be possible to
          break anything (e.g. a copy change, or modifying a numeric constant).
        </Li>

        <Li>
          <Strong>types</Strong>: Improvements to type annotations/testing (i.e.
          related to TypeScript, PHPStan) without impacting runtime behaviour
        </Li>
      </Ul>

      <P>
        Keep your titles short (70 chars is the limit before GitHub breaks it
        off).
      </P>

      <P>
        If the project is a monorepo with multiple packages, and the change only
        relates to one package, you should reference it in brackets after the
        type, e.g.:
      </P>

      <Blockquote>feat(web): allow spaces in phone</Blockquote>

      <P>
        If the fix relates to a client-facing maintenance ticket, or a Sentry
        error, reference it at the end, e.g.:
      </P>

      <Blockquote>
        fix: don't allow letters in phone number (HUB-156)
      </Blockquote>
      <Blockquote>fix: avoid crash on empty UsersList (SENTRY-G3T)</Blockquote>

      <P>
        Or, if the issue relates to one of our internal Trello tickets, link to
        it in the PR description. If you are making a PR for some other reason,
        explain why in the PR description.
      </P>

      <H2>Handling Pull Request feedback</H2>

      <P>
        Only mark your Pull Request as "Ready for Review" when it's in a state
        where it's ready to merge - otherwise use the draft state.
      </P>

      <P>
        Use the "Request Review" feature when submitting for the first time, or
        you've actioned changes and it's ready to re-review.
      </P>

      <P>
        If you have understood and addressed a point raised in feedback and
        pushed the changes, you can mark the conversation as resolved. If you're
        not sure, leave it unresolved, and explain why in a comment reply.
      </P>
    </>
  );
}
